import { AxiosResponse } from 'axios';
import { getApi } from 'api';
// Kiosk-token requests whose server text is localized first await kioskLanguageService.settle(), so a
// language picked a moment ago is saved on the session before the server answers in it.
import kioskLanguageService from 'services/kiosk-language-service';
import { IKioskActivateResponse, IKioskSessionResponse } from 'types/kiosk/IKioskSessionResponse';
import { TActivateKiosk } from 'types/kiosk/TActivateKiosk';
import { TDeactivateKiosk } from 'types/kiosk/TDeactivateKiosk';
import { TKioskClockIn } from 'types/kiosk/TKioskClockIn';
import { TKioskClockOut } from 'types/kiosk/TKioskClockOut';
import { TKioskVerifyPinResult } from 'types/kiosk/TKioskVerifyPinResult';
import { TTimeRegistration } from 'types/kiosk/TTimeRegistration';
import { getKioskTimezoneOffset, KIOSK_TIMEZONE } from 'utils/kiosk-timezone';

/**
 * Activate a kiosk session. Authenticated with the manager's bearer token, which the global
 * request interceptor attaches automatically (the manager is still logged in at this point).
 */
export const activateKioskSessionRequest = async (body: TActivateKiosk): Promise<IKioskActivateResponse> => {
  const response: AxiosResponse<IKioskActivateResponse> = await getApi().post('KioskSession/Activate', body);
  return response.data;
};

/**
 * Re-validate a persisted kiosk session. The kiosk token is sent explicitly per-request
 * (the manager token is gone once kiosk mode is active).
 */
export const getCurrentKioskSessionRequest = async (kioskToken: string): Promise<IKioskSessionResponse> => {
  const response: AxiosResponse<IKioskSessionResponse> = await getApi().get('KioskSession/Current', {
    headers: { Authorization: `Bearer ${kioskToken}` },
  });
  return response.data;
};

/** End a kiosk session. Kiosk token authenticates; manager credentials in the body authorise the exit. */
export const deactivateKioskSessionRequest = async (kioskToken: string, body: TDeactivateKiosk): Promise<void> => {
  await getApi().post('KioskSession/Deactivate', body, {
    headers: { Authorization: `Bearer ${kioskToken}` },
  });
};

/**
 * Fetch the kiosk selection feed (active / planned / all employees) for the bound location.
 * Auth + scope come from the kiosk JWT; we send the (Belgium) timezone for the server-side planned window.
 * Returns the raw body — the caller maps it via fromResponseToKioskSelectionFeed.
 */
export const getKioskSelectionFeedRequest = async (kioskToken: string): Promise<unknown> => {
  await kioskLanguageService.settle();
  const response = await getApi().get('KioskSession/SelectionFeed', {
    headers: {
      Authorization: `Bearer ${kioskToken}`,
      'X-Timezone': KIOSK_TIMEZONE,
      'X-Timezone-Offset': getKioskTimezoneOffset(),
    },
  });
  return response.data;
};

/**
 * Fetch the start-registration data for the tapped employee (plannings, cost centres, prefills, policies).
 * Auth + company/location/language come from the kiosk JWT; we send the (Belgium) timezone for the
 * local "today" eligibility window. Returns the raw body — the caller maps it.
 */
export const getKioskStartRegistrationDataRequest = async (
  kioskToken: string,
  employeeId: number,
): Promise<unknown> => {
  await kioskLanguageService.settle();
  const response = await getApi().get('KioskSession/StartRegistrationData', {
    params: { employeeId },
    headers: {
      Authorization: `Bearer ${kioskToken}`,
      'X-Timezone': KIOSK_TIMEZONE,
    },
  });
  return response.data;
};

/**
 * Start a time registration (clock-in) for the selected employee. The server forces
 * registrationMethod = "Kiosk" and uses the kiosk's bound device + (when bound) cost centre.
 * Sends both timezone headers so the backend can convert local date+startWork to UTC.
 */
export const kioskClockInRequest = async (kioskToken: string, body: TKioskClockIn): Promise<TTimeRegistration> => {
  await kioskLanguageService.settle();
  const response: AxiosResponse<TTimeRegistration> = await getApi().post('KioskSession/ClockIn', body, {
    headers: {
      Authorization: `Bearer ${kioskToken}`,
      'X-Timezone': KIOSK_TIMEZONE,
      'X-Timezone-Offset': getKioskTimezoneOffset(),
    },
  });
  return response.data;
};

/** Sentinel returned by getKioskClockOutDataRequest on a 204:
 *  the employee has no active registration → the kiosk routes them to clock-in. */
export const KIOSK_NO_ACTIVE_REGISTRATION = Symbol('KIOSK_NO_ACTIVE_REGISTRATION');

/**
 * Fetch the end-registration prefill for the tapped employee's active registration.
 * Returns the raw body (caller maps it), or KIOSK_NO_ACTIVE_REGISTRATION on 204.
 * Auth + company/location/language come from the kiosk JWT; we send the device UTC offset.
 */
export const getKioskClockOutDataRequest = async (
  kioskToken: string,
  employeeId: number,
): Promise<unknown | typeof KIOSK_NO_ACTIVE_REGISTRATION> => {
  await kioskLanguageService.settle();
  const response = await getApi().get('KioskSession/ClockOutData', {
    params: { employeeId },
    headers: {
      Authorization: `Bearer ${kioskToken}`,
      'X-Timezone-Offset': getKioskTimezoneOffset(),
    },
  });
  if (response.status === 204) return KIOSK_NO_ACTIVE_REGISTRATION;
  return response.data;
};

/**
 * End (clock out) the tapped employee's active registration, reusing the shared backend
 * clock-out rules. Sends the device UTC offset so the backend converts local date/endWork/breaks.
 */
export const kioskClockOutRequest = async (kioskToken: string, body: TKioskClockOut): Promise<TTimeRegistration> => {
  await kioskLanguageService.settle();
  const response: AxiosResponse<TTimeRegistration> = await getApi().post('KioskSession/ClockOut', body, {
    headers: {
      Authorization: `Bearer ${kioskToken}`,
      'X-Timezone-Offset': getKioskTimezoneOffset(),
    },
  });
  return response.data;
};

/**
 * Verify a tapped employee's PIN. On success the response carries a short-lived (3 min)
 * `pinVerificationToken` bound to this session + employee, required by ClockIn/ClockOut on
 * PIN-gated sessions. `valid: false` never throws — `reason` distinguishes a wrong PIN from a
 * (possibly stale) missing PIN.
 */
export const verifyKioskPinRequest = async (
  kioskToken: string,
  employeeId: number,
  pin: string,
): Promise<TKioskVerifyPinResult> => {
  await kioskLanguageService.settle();
  const response: AxiosResponse<TKioskVerifyPinResult> = await getApi().post(
    'KioskSession/VerifyPin',
    { employeeId, pin },
    { headers: { Authorization: `Bearer ${kioskToken}` } },
  );
  return response.data;
};
